import React, { useEffect, useState, useMemo } from 'react';
import { Col, Card, Button } from 'react-bootstrap';
//slices
import { setTotalWithDiscount } from 'slices/cart';
//icons
import { RiAlertFill, RiInformationLine } from 'react-icons/ri';
//translations
import { useTranslation } from 'react-i18next';
// Redux
import { useSelector, useDispatch } from 'react-redux';
import { cartSelector, setErrorAmountPrice } from 'slices/cart';
// Cart Utils
import { formatCurrency } from 'utils/currencyFormatter';
import {
  calculateTotalOrderSummary,
  calculateDiscountAmount,
  calculateTotalOrderWithDiscount,
} from 'components/cart/utils';

const OrderSummary = ({ activeStep = null }) => {
  const { t } = useTranslation(['cart']);
  const dispatch = useDispatch();
  const {
    countItems,
    subtotal,
    iva,
    total,
    shippingCosts,
    discountCart,
    isShippingAvailable,
    errorAmountPrice,
    giftCard,
  } = useSelector(cartSelector);

  const hasGiftCard = giftCard && giftCard.discount_amount > 0;

  const ivaConGiftCard = useMemo(() => {
    if (hasGiftCard) {
      const extraIVA = total * 0.04;
      return extraIVA;
    }
    return iva;
  }, [iva, total, hasGiftCard]);

  const subtotalToShow = useMemo(() => {
    if (hasGiftCard) {
      return total - total * 0.04;
    }
    return subtotal;
  }, [hasGiftCard, total, subtotal]);

  const amountDiscount = useMemo(() => {
    const baseSubtotal = hasGiftCard ? subtotalToShow : subtotal;
    const baseIVA = hasGiftCard ? ivaConGiftCard : iva;

    if (discountCart?.type === 'percentage') {
      return calculateDiscountAmount(baseSubtotal, baseIVA, discountCart?.quantity);
    } else if (discountCart?.type === 'gift_card') {
      return discountCart?.quantity;
    }
    return 0;
  }, [discountCart, subtotal, iva, hasGiftCard, subtotalToShow, ivaConGiftCard]);

  useEffect(() => {
    console.log('Order Summary Data:', {
      countItems,
      subtotal,
      iva,
      total,
      shippingCosts,
      discountCart,
      isShippingAvailable,
      totalOrderSummaryDiscount,
    });
  }, []);

  const totalOrderSummaryDiscount = useMemo(() => {
    let totalConDescuento;
    if (hasGiftCard) {
      totalConDescuento = subtotalToShow + ivaConGiftCard - amountDiscount + shippingCosts;
    } else {
      totalConDescuento = calculateTotalOrderWithDiscount(subtotal, iva, amountDiscount, shippingCosts);
    }
    return Math.max(0, totalConDescuento).toFixed(2);
  }, [amountDiscount, subtotal, iva, shippingCosts, hasGiftCard, ivaConGiftCard, total, subtotalToShow]);

  useEffect(() => {
    dispatch(setErrorAmountPrice(Number(totalOrderSummaryDiscount) < 0));
  }, [totalOrderSummaryDiscount, dispatch]);

  useEffect(() => {
    const totalFinal = Number(totalOrderSummaryDiscount);
    dispatch(setTotalWithDiscount(Math.max(0, totalFinal)));
  }, [hasGiftCard, discountCart, totalOrderSummaryDiscount, total, dispatch]);

  return (
    <Col md={3} className="mb-2 mt-3 text-muted priceCol">
      <Card>
        <Card.Body>
          <div className="aside-title mb-3 px-2">
            <span>{t('yourOrder')}</span>
          </div>
          <Card.Subtitle className="mb-3 pl-3 text-muted">
            {countItems} {t('articles')}
          </Card.Subtitle>
          <Col className="mt-4 d-flex justify-content-between">
            <p>{t('subtotal')}</p>
            <p>{subtotalToShow ? formatCurrency(subtotalToShow) : ''}</p>
          </Col>
          <Col className="d-flex justify-content-between">
            <p>{t('VAT')}</p>
            <p>{ivaConGiftCard ? formatCurrency(ivaConGiftCard) : ''}</p>
          </Col>
          {(!discountCart?.has_discount || discountCart?.type === 'gift_card') && (
            <Col className="d-flex justify-content-between">
              <p>{t('shippingCosts')}</p>
              <p>{shippingCosts ? formatCurrency(shippingCosts) : <b>{t('freeShipping')}</b>}</p>
            </Col>
          )}
          <Col className="borderDiscount  d-flex justify-content-between">
            <p>{discountCart?.type === 'percentage' ? t('totalWithoutDiscount') : t('total')}</p>
            {formatCurrency(calculateTotalOrderSummary(discountCart, total, shippingCosts))}
          </Col>
          {discountCart?.type === 'percentage' && (
            <>
              <Col className=" d-flex justify-content-between textDiscount">
                <p>{t('discount')}</p>
                <p>{`${discountCart.quantity} %`} </p>
              </Col>
              <Col className=" d-flex justify-content-between textDiscount">
                <p>{t('discountAmount')}</p>
                <p>{formatCurrency(amountDiscount)}</p>
              </Col>
              <Col className="d-flex justify-content-between">
                <p>{t('shippingCosts')}</p>
                <p>{shippingCosts ? formatCurrency(shippingCosts) : <b>{t('freeShipping')}</b>}</p>
              </Col>
              <Col className=" d-flex justify-content-between textDiscount borderDiscount">
                <p>{t('totalWithDiscount')}</p>
                <p>{formatCurrency(totalOrderSummaryDiscount)}</p>
              </Col>
            </>
          )}
          {discountCart?.type === 'gift_card' && (
            <>
              <Col className=" d-flex justify-content-between textDiscount">
                <p>{t('discountGiftCard')}</p>
                <p>{formatCurrency(discountCart.quantity)}</p>
              </Col>
              <Col className=" d-flex justify-content-between textDiscount borderDiscount">
                <p className={errorAmountPrice && 'text-danger'}>{t('totalWithDiscount')}</p>
                <p className={errorAmountPrice && 'text-danger'}>{formatCurrency(totalOrderSummaryDiscount)}</p>
              </Col>
              {Number(totalOrderSummaryDiscount) <= 0 && (
                <div className="text-center mt-3">
                  <Button variant="success" /* onClick={paymentByGiftCard} */>{t('Usar tarjeta de regalo')}</Button>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
      {activeStep === 0 && (
        <p>
          <RiInformationLine className="icon-md" /> {t('shippingNote')}
        </p>
      )}
      {activeStep === 1 && !isShippingAvailable?.availability && (
        <p>
          <RiAlertFill className="icon-md" /> {t(`${isShippingAvailable?.message}`)}
        </p>
      )}
      {errorAmountPrice && (
        <p className="text-danger mt-2">
          <RiAlertFill className="icon-md" /> {t('errorPriceAmount')}
        </p>
      )}
    </Col>
  );
};

export default OrderSummary;
